import React, { useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useMotionTemplate,
} from "framer-motion";
import { Box, Typography, Stack, Container } from "@mui/material";
import ProjectDialog from "./projectDialog";
import { projects } from "./projects";
import BgText from "./bgText";

const ProjectListItem = ({ project, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Mouse tracking for gradient effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth the mouse movement
  const springX = useSpring(mouseX, { damping: 30, stiffness: 150 });
  const springY = useSpring(mouseY, { damping: 30, stiffness: 150 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left);
    mouseY.set(e.clientY - rect.top);
  };

  // Construct the dynamic background gradient
  const background = useMotionTemplate`radial-gradient(300px circle at ${springX}px ${springY}px, #fff 0%, #22d3ee 40%, #818cf8 80%, transparent 100%)`;

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, margin: "-100px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick(project)}
      sx={{
        py: 6,
        borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
        cursor: "pointer",
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Stack
        direction="row"
        spacing={6}
        alignItems="center"
        sx={{ position: "relative" }}
      >
        <Box sx={{ position: "relative" }}>
          {/* BASE LAYER */}
          <Typography
            variant="h1"
            sx={{
              fontFamily: "Outfit",
              fontWeight: 900,
              fontSize: { xs: "3rem", md: "6rem" },
              lineHeight: 1.175,
              letterSpacing: "-0.02em",
              color: "rgba(255, 255, 255, 0.15)",
              pointerEvents: "none",
            }}
          >
            {project.title}
          </Typography>

          {/* HIGHLIGHT LAYER */}
          <Typography
            component={motion.h1}
            variant="h1"
            style={{
              backgroundImage: background,
              opacity: isHovered ? 1 : 0,
            }}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              fontFamily: "Outfit",
              fontWeight: 900,
              fontSize: { xs: "3rem", md: "6rem" },
              lineHeight: 1.175,
              letterSpacing: "-0.02em",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              pointerEvents: "none",
              transition: "opacity 0.4s ease",
              zIndex: 1,
              whiteSpace: "nowrap",
            }}
          >
            {project.title}
          </Typography>
        </Box>
      </Stack>

      <Typography
        variant="h6"
        sx={{
          color: "rgba(255,255,255,0.15)",
          fontFamily: "Outfit",
          fontWeight: 400,
          display: { xs: "none", md: "block" },
        }}
      >
        {project.stack.split(",")[0]}
      </Typography>
    </Box>
  );
};

const ProjectList = () => {
  const [projectDialogOpen, setDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(projects[0]);
  const [alertOpen, setAlertOpen] = useState(false);

  const openProject = (project) => {
    setSelectedProject(project);
    setDialogOpen(true);
  };

  return (
    <Stack
      sx={{
        width: "100%",
        minHeight: "100vh",
        alignItems: "center",
        position: "relative",
        pt: 8,
        pb: 20,
        zIndex: 1,
      }}
      id="projects"
    >
      <BgText>Archive</BgText>

      <Container maxWidth="xl">
        <Stack width="100%">
          {projects.map((project) => (
            <ProjectListItem
              key={project.title}
              project={project}
              onClick={openProject}
            />
          ))}
        </Stack>
      </Container>

      <ProjectDialog
        open={projectDialogOpen}
        setOpen={setDialogOpen}
        selectedProject={selectedProject}
        setAlertOpen={setAlertOpen}
      />
    </Stack>
  );
};

export default ProjectList;
